import type {
    FormArray,
    AbstractControl,
    FormControl,
    FormGroup,
    FormGroupDirective,
    NgForm,
} from '@angular/forms';
import type { Field, FieldTree } from '@angular/forms/signals';
import type { ErrorStateMatcher as ESM } from '@angular/material/core';
import type { Observable } from 'rxjs';

export type FormValues<T> =
    T extends FormGroup<infer TControls>
        ? { [K in keyof TControls]: FormValues<TControls[K]> }
        : T extends FormArray<infer TControl>
          ? FormValues<TControl>[]
          : T extends FormControl<infer TValue>
            ? TValue
            : never;

export interface SignalFormState<TModel, TPayload> {
    readonly form: FieldTree<TModel>;
    validateAndBuildPayload(): Observable<TPayload>;
}

/**
 * Custom error state matcher that checks if the control is invalid and dirty or touched or submitted.
 * Native fields use dirty/touched visibility; submission owners mark fields touched.
 * Reactive controls additionally support submitted state and parent errorKeys.
 * Native workflow/group errors remain owned by the feature matcher.
 */
export class ErrorStateMatcher implements ESM {
    private readonly onDirty: boolean;
    private readonly onTouched: boolean;
    private readonly errorKeys: string[];

    constructor(
        options: Partial<{
            onDirty: boolean;
            onTouched: boolean;
            errorKeys: string | string[];
        }> = {}
    ) {
        this.onDirty = options.onDirty ?? true;
        this.onTouched = options.onTouched ?? true;

        if (!options.errorKeys) {
            this.errorKeys = [];
        } else {
            this.errorKeys = Array.isArray(options.errorKeys)
                ? options.errorKeys
                : [options.errorKeys];
        }
    }

    isSignalErrorState(field: Field<unknown> | null): boolean {
        if (!field) return false;
        const state = field();
        return (
            state.invalid() &&
            ((this.onDirty && state.dirty()) ||
                (this.onTouched && state.touched()))
        );
    }

    isErrorState(
        control: AbstractControl | null,
        form: FormGroupDirective | NgForm | null
    ): boolean {
        const isSubmitted = !!form?.submitted;
        const controlInvalid =
            control?.invalid &&
            ((this.onDirty && control.dirty) ||
                (this.onTouched && control.touched) ||
                isSubmitted);

        if (!this.errorKeys.length) return !!controlInvalid;

        const parentHasError = this.errorKeys.some(
            (key) =>
                control?.parent?.hasError(key) &&
                (control.dirty || control.touched || isSubmitted)
        );

        return !!(controlInvalid || parentHasError);
    }
}
